import React, { useState, useCallback } from 'react';
import { Modal, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { importarExcelReembolsoIptu } from '~/Services/Service';

const formatMoeda = (val) =>
  val != null
    ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const ModalImportarExcel = ({ show, onClose, onAplicar }) => {
  const [linhas, setLinhas] = useState([]);
  const [erros, setErros] = useState([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const arquivo = acceptedFiles[0];
    const formData = new FormData();
    formData.append('arquivo', arquivo);

    setUploading(true);
    setLinhas([]);
    setErros([]);

    importarExcelReembolsoIptu(formData)
      .then((res) => {
        setLinhas(res.linhas || []);
        setErros(res.erros || []);
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Erro ao importar planilha';
        toast.error(msg);
      })
      .finally(() => setUploading(false));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.xlsx',
    multiple: false,
    disabled: uploading,
  });

  const handleAplicar = () => {
    if (!linhas.length) return;
    onAplicar(linhas);
    handleClose();
  };

  const handleClose = () => {
    setLinhas([]);
    setErros([]);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Importar Excel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          {...getRootProps()}
          style={{
            border: '2px dashed #6c757d',
            borderRadius: '4px',
            padding: '24px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            background: isDragActive ? '#e9ecef' : '#f8f9fa',
            marginBottom: '16px',
          }}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <Spinner animation="border" size="sm" />
          ) : isDragActive ? (
            <p className="mb-0">Solte o arquivo aqui...</p>
          ) : (
            <p className="mb-0">
              Arraste um arquivo <strong>.xlsx</strong> ou clique para selecionar
            </p>
          )}
        </div>

        {erros.length > 0 && (
          <Alert variant="danger">
            <strong>Erros na importação:</strong>
            <ul className="mb-0 mt-1">
              {erros.map((e, idx) => (
                <li key={`err-${idx}`}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}

        {linhas.length > 0 && (
          <>
            <p className="text-muted small mb-2">
              {linhas.length} linha(s) importada(s). Verifique os dados e clique em Aplicar.
            </p>
            <div className="table-responsive">
              <Table striped bordered size="sm">
                <thead className="thead-light">
                  <tr>
                    <th>Cód. Ativo</th>
                    <th>Parcelas</th>
                    <th>Valor Total</th>
                    <th>Valor Parcela</th>
                    <th>1º Vencimento</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr key={`linha-${l.codigo_ativo}`}>
                      <td>{l.codigo_ativo}</td>
                      <td>{l.parcelas}</td>
                      <td>{formatMoeda(l.valor_total)}</td>
                      <td>{formatMoeda(l.valor_parcela)}</td>
                      <td>{l.primeiro_vencimento || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleAplicar}
          disabled={!linhas.length || uploading}
        >
          Aplicar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalImportarExcel;
